import type { ReactNode } from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import type { AlignmentValue } from '@elfsight/configurator-contracts';
import type { BlockProps } from '@elfsight/widget-banner/widget';
import {
  Banner,
  blockComponents as defaultElementComponents,
  useApplyMobileSettings
} from '@elfsight/widget-banner/widget';
import { useViewportWidth } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { BUILDER_CONTENT_MIN_WIDTH } from '../../../constants';
import type { AlignmentPane } from '../../../types';
import {
  checkIsBar,
  checkIsPane,
  checkIsSlide,
  getContentPosition,
  getDirectionBar,
  useThumbnailMode
} from '../../../utils';
import { data } from '../../banner';
import { Deactivation } from '../../Deactivation';
import { FREE_LINK_HEIGHT_ON_MOBILE, FreeLink } from '../free-link';

const finalElementComponents = {
  ...defaultElementComponents,
  ...data
};

const VERTICAL_HEIGHT_AUTO = { auto: true, customValue: 0 };

type ContentProps = {
  elements: BlockProps[];
  elementsAlignment?: AlignmentValue;
  elementsAlignmentPane?: AlignmentPane;
  controlClose?: ReactNode;
  popupShowing: boolean;
};

export function Content({
  elements,
  elementsAlignment = 'left',
  elementsAlignmentPane = { position: 'top-left' },
  controlClose,
  popupShowing
}: ContentProps) {
  const {
    widgetWidth,
    widgetBarHeight,
    layout,
    widgetBackground: derivedWidgetBackground,
    widgetPadding,
    widgetPaddingOnMobile,
    elementsGap,
    elementsGapOnMobile,
    widgetLink,
    widgetId = 'widget-id',
    editMode = false,
    googleAnalytics,
    widgetToken = '',
    displayDeactivation,
    displayFreeLink
  } = useSettings();

  const {
    styles: { backgroundColor: thumbnailBackground },
    thumbnailMode
  } = useThumbnailMode();
  const isThumbnailBackground = thumbnailMode && thumbnailBackground;

  const bannerElementsAlignment = getContentPosition(
    layout,
    elementsAlignment,
    elementsAlignmentPane
  );

  const applyMobileSettings = useApplyMobileSettings(widgetWidth);
  const viewportWidth = useViewportWidth(true);
  const maxContentWidth = viewportWidth
    ? Math.min(widgetWidth, viewportWidth)
    : widgetWidth;

  const selectContentIndents = (indent: number, mobileIndent: number) => {
    if (applyMobileSettings) {
      return mobileIndent;
    }

    return indent;
  };

  const direction = getDirectionBar(layout);
  const isRow = direction === 'row';

  const widgetBackground = useMemo(() => {
    const backgroundCopy = JSON.parse(JSON.stringify(derivedWidgetBackground));

    if (checkIsPane(layout)) {
      backgroundCopy.borderRadius = 0;
    }

    return backgroundCopy;
  }, [layout, derivedWidgetBackground]);

  const isSlide = checkIsSlide(layout);
  const isBar = checkIsBar(layout);

  const contentPadding = selectContentIndents(
    widgetPadding,
    widgetPaddingOnMobile
  );

  return (
    <>
      {isSlide && (
        <Close>
          <CloseInner>{controlClose}</CloseInner>
        </Close>
      )}

      <Deactivation
        containerStyle={{ height: '100%' }}
        displayDeactivation={displayDeactivation && !isBar}
      >
        <Banner
          googleAnalytics={googleAnalytics}
          editMode={editMode}
          blocks={elements}
          components={finalElementComponents}
          contentDirection={direction}
          bannerBackground={
            isRow || isThumbnailBackground ? null : widgetBackground
          }
          bannerLink={widgetLink}
          horizontalBannerHeight={widgetBarHeight}
          verticalBannerHeight={VERTICAL_HEIGHT_AUTO}
          stretchBackground={isRow}
          bannerWidth={isRow ? 2880 : maxContentWidth}
          contentAlignment={bannerElementsAlignment}
          contentPadding={contentPadding}
          contentVerticalPadding={
            displayFreeLink
              ? [contentPadding, FREE_LINK_HEIGHT_ON_MOBILE]
              : undefined
          }
          contentSpacing={selectContentIndents(
            elementsGap,
            elementsGapOnMobile
          )}
          style={{
            minWidth: `${BUILDER_CONTENT_MIN_WIDTH}px`
          }}
          widgetToken={widgetToken}
          widgetId={widgetId}
          submitAdditionalData={{
            type: 'popup'
          }}
        />
      </Deactivation>

      <FreeLink popupShowing={popupShowing} />
    </>
  );
}

const Close = styled.div`
  position: sticky;
  top: 0;
  height: 0;
  z-index: 4;
`;

const CloseInner = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;
